// App-wide confirmation dialog, replacing the browser's native confirm().
//
// `confirmDialog({...})` resolves true (confirmed) or false (cancelled,
// Escape, backdrop click). Requests queue: a second one waits until the first
// is answered, so two quick clicks can never stack two dialogs. The single
// <ConfirmDialog /> host in main.tsx renders the head of the queue.

import { create } from 'zustand';

export type ConfirmTone = 'danger' | 'default';

export interface ConfirmOptions {
  title: string;
  /** Detail under the title. Blank lines start a new paragraph. */
  message?: string;
  /** Defaults to "Delete" for `danger`, "Confirm" otherwise. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` (default) for destructive or irreversible actions. */
  tone?: ConfirmTone;
}

export interface ConfirmRequest extends Required<Omit<ConfirmOptions, 'message'>> {
  id: number;
  message: string | null;
}

interface ConfirmState {
  queue: ConfirmRequest[];
  /** Answer the request with this id and move on to the next one. */
  settle: (id: number, confirmed: boolean) => void;
}

let nextId = 1;
const resolvers = new Map<number, (confirmed: boolean) => void>();

export const useConfirmStore = create<ConfirmState>((set) => ({
  queue: [],
  settle: (id, confirmed) => {
    const resolve = resolvers.get(id);
    resolvers.delete(id);
    set((s) => ({ queue: s.queue.filter((r) => r.id !== id) }));
    resolve?.(confirmed);
  },
}));

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  const tone = options.tone ?? 'danger';
  const request: ConfirmRequest = {
    id: nextId++,
    title: options.title,
    message: options.message ?? null,
    confirmLabel: options.confirmLabel ?? (tone === 'danger' ? 'Delete' : 'Confirm'),
    cancelLabel: options.cancelLabel ?? 'Cancel',
    tone,
  };
  return new Promise<boolean>((resolve) => {
    resolvers.set(request.id, resolve);
    useConfirmStore.setState((s) => ({ queue: [...s.queue, request] }));
  });
}
